/**
 * Enhanced cache with localStorage persistence for API responses
 */

import { autoCleanupLocalStorage } from './clearLocalStorage'
import { aggressiveCacheClear } from './clearAllStorage'

interface CacheEntry<T> {
  data: T
  timestamp: number
  expiresAt: number
}

class MemoryCache {
  private cache: Map<string, CacheEntry<any>> = new Map()
  private defaultTTL = 15 * 60 * 1000 // 15 minutes default TTL
  private persistentKeys = ['public_research', 'user_research'] // Keys to persist in localStorage
  private localStorageEnabled = true
  private failureCount = 0

  /**
   * Get cached data if it exists and hasn't expired
   * First check memory cache, then localStorage for persistent keys
   */
  get<T>(key: string): T | null {
    // Check memory cache first
    let entry = this.cache.get(key)
    
    // If not in memory, check localStorage for persistent keys
    if (!entry && this.shouldPersist(key)) {
      try {
        const stored = localStorage.getItem(`cache_${key}`)
        if (stored) {
          entry = JSON.parse(stored) as CacheEntry<T>
          // Restore to memory cache if still valid
          if (entry && Date.now() <= entry.expiresAt) {
            this.cache.set(key, entry)
          }
        }
      } catch (e) {
        console.warn('Failed to read from localStorage:', e)
      }
    }
    
    if (!entry) {
      return null
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.invalidate(key)
      return null
    }

    console.log(`[Cache HIT] ${key}`)
    return entry.data as T
  }

  /**
   * Set data in cache with optional TTL
   * Also persist to localStorage for important keys
   */
  set<T>(key: string, data: T, ttlMs?: number): void {
    const ttl = ttlMs || this.defaultTTL
    const now = Date.now()
    const entry = {
      data,
      timestamp: now,
      expiresAt: now + ttl
    }
    
    this.cache.set(key, entry)
    console.log(`[Cache SET] ${key} (TTL: ${ttl / 1000}s)`)
    
    // Persist to localStorage if needed (and if enabled)
    // Skip caching large payloads (>100KB) to avoid localStorage issues
    const entrySize = JSON.stringify(entry).length
    if (this.localStorageEnabled && this.shouldPersist(key) && entrySize < 100000) {
      try {
        localStorage.setItem(`cache_${key}`, JSON.stringify(entry))
        this.failureCount = 0 // Reset failure count on success
      } catch (e) {
        if (e instanceof Error && e.name === 'QuotaExceededError') {
          this.failureCount++
          
          // Try aggressive cleanup on first failure
          if (this.failureCount === 1) {
            console.warn('localStorage full, attempting aggressive cleanup...')
            const removed = aggressiveCacheClear()
            
            if (removed > 0) {
              // Try one more time after cleanup
              try {
                localStorage.setItem(`cache_${key}`, JSON.stringify(entry))
                this.failureCount = 0
              } catch {
                console.warn('Cache persistence failed even after cleanup')
              }
            }
          }
          
          // Disable localStorage caching after 3 failures
          if (this.failureCount >= 3) {
            console.warn('Disabling localStorage caching due to repeated failures')
            this.localStorageEnabled = false
          }
        } else {
          console.warn('Failed to persist to localStorage:', e)
        }
      }
    }

    // Clean up old entries periodically
    if (this.cache.size > 100) {
      this.cleanup()
    }
  }

  /**
   * Invalidate specific cache entry
   */
  invalidate(key: string): void {
    this.cache.delete(key)
    inFlightRequests.delete(key)
    // Also remove from localStorage
    if (this.shouldPersist(key)) {
      try {
        localStorage.removeItem(`cache_${key}`)
      } catch (e) {
        console.warn('Failed to remove from localStorage:', e)
      }
    }
  }

  /**
   * Invalidate all cache entries matching a pattern
   */
  invalidatePattern(pattern: string | RegExp): void {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern
    
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key)
        inFlightRequests.delete(key)
        if (this.shouldPersist(key)) {
          try {
            localStorage.removeItem(`cache_${key}`)
          } catch (e) {
            console.warn('Failed to remove from localStorage:', e)
          }
        }
      }
    }
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear()
    inFlightRequests.clear()
    // Clear localStorage cache entries
    try {
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith('cache_')) {
          localStorage.removeItem(key)
        }
      })
    } catch (e) {
      console.warn('Failed to clear localStorage cache:', e)
    }
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now()
    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.cache.delete(key)
      }
    }
  }

  /**
   * Check if a key should be persisted to localStorage
   */
  private shouldPersist(key: string): boolean {
    return this.persistentKeys.some(pk => key.startsWith(pk))
  }
  
  /**
   * Clear old localStorage cache entries
   */
  private clearOldLocalStorage(): void {
    try {
      const now = Date.now()
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith('cache_')) {
          const stored = localStorage.getItem(key)
          if (stored) {
            try {
              const entry = JSON.parse(stored) as CacheEntry<any>
              if (now > entry.expiresAt) {
                localStorage.removeItem(key)
              }
            } catch {
              // Invalid entry, remove it
              localStorage.removeItem(key)
            }
          }
        }
      })
    } catch (e) {
      console.warn('Failed to clear old localStorage entries:', e)
    }
  }
  
  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: string[]; localStorage: number } {
    let localStorageCount = 0
    try {
      localStorageCount = Object.keys(localStorage).filter(k => k.startsWith('cache_')).length
    } catch {}
    
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      localStorage: localStorageCount
    }
  }
}

// Export singleton instance
export const apiCache = new MemoryCache()

// Cache key generators
export const cacheKeys = {
  publicResearch: (limit: number, offset: number) => 
    `public_research_${limit}_${offset}`,
  
  userResearch: (userId: number, limit: number, offset: number, includePrivate: boolean, bucket: string = 'all') => 
    `user_research_${userId}_${limit}_${offset}_${includePrivate}_${bucket}`,
  
  researchDetail: (id: number) => 
    `research_detail_${id}`,
  
  savedResearch: (userId?: number | null) => 
    userId ? `saved_research_${userId}` : 'saved_research_anonymous',
  
  userProfile: (email: string) => 
    `user_profile_${email}`
}

// Track in-flight requests to prevent duplicate API calls
const inFlightRequests: Map<string, Promise<any>> = new Map()

/**
 * Clear all in-flight requests. Must be called on user change
 * to prevent a previous user's pending response from being cached
 * under the new user's session.
 */
export function clearInFlightRequests(): void {
  inFlightRequests.clear()
}

// Helper function for cached API calls with request deduplication
export async function cachedApiCall<T>(
  cacheKey: string,
  apiCall: () => Promise<T>,
  ttlMs?: number
): Promise<T> {
  // Check cache first
  const cached = apiCache.get<T>(cacheKey)
  if (cached !== null) {
    // console.log(`Cache hit for ${cacheKey}`)
    return cached
  }

  // Check if there's already an in-flight request for this key
  const inFlight = inFlightRequests.get(cacheKey)
  if (inFlight) {
    // console.log(`Request deduplication for ${cacheKey}`)
    return inFlight as Promise<T>
  }

  // Make API call and track it
  // console.log(`Cache miss for ${cacheKey}, fetching...`)
  const requestPromise = apiCall().then(data => {
    // Store in cache
    apiCache.set(cacheKey, data, ttlMs)
    // Remove from in-flight tracking
    inFlightRequests.delete(cacheKey)
    return data
  }).catch(error => {
    // Remove from in-flight tracking on error
    inFlightRequests.delete(cacheKey)
    throw error
  })
  
  // Track this request
  inFlightRequests.set(cacheKey, requestPromise)
  
  return requestPromise
}
